import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import ProductCard from "../../components/common/ProductCardComponent/Index";
import DefaultHeader from "../../assests/02 (1).png";
import FilterSidebar from "../../components/FilterSidebar/index";
import ReactPaginate from 'react-paginate';
import { fetchProductsByCategory, fetchProductsBySubCategory } from '../../Services/CategoryServices';
import Spinner from "../../components/common/loadingSpinnercomponent/Spinner";
import './Categories.css';
import { Image_URL } from "../../config/config";

// Add the base path for category images
const CATEGORY_IMAGE_PATH = Image_URL + 'categories/';

const CategoryList = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categoryName, setCategoryName] = useState("");
    const [categoryImage, setCategoryImage] = useState(null);
    const itemsPerPage = 8;
    const { slug } = useParams();

    // Define the state for filters
    const [filters, setFilters] = useState({
        category: null,
        size: null,
        availability: null,
        minPrice: null,
        maxPrice: null,
    });

    // State for sorting
    const [sortOption, setSortOption] = useState('latest');

    /**
     * Initialize filters and sort option from URL when component mounts
     */
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const initialFilters = {
            category: urlParams.get('category') || null,
            size: urlParams.get('size') || null,
            availability: urlParams.get('availability') || null,
            minPrice: urlParams.get('minprice') ? parseInt(urlParams.get('minprice'), 10) : null,
            maxPrice: urlParams.get('maxprice') ? parseInt(urlParams.get('maxprice'), 10) : null,
        };
        const initialSort = urlParams.get('sort') || 'latest';
        setFilters(initialFilters);
        setSortOption(initialSort);
    }, []);

    /**
     * Handle filter changes from FilterSidebar
     */
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        console.log('Updated Filters:', newFilters);
    };

    /**
     * Build query parameters string based on filters
     */
    const buildQueryParams = (filters) => {
        const params = new URLSearchParams();

        params.set('category', filters.category || '');
        params.set('size', filters.size || '');
        params.set('availability', filters.availability || '');
        params.set('minprice', filters.minPrice !== null ? filters.minPrice : '');
        params.set('maxprice', filters.maxPrice !== null ? filters.maxPrice : '');

        return params.toString();
    };

    /**
     * Update URL with current query string and sort option
     */
    const updateUrlWithQueryString = (queryString, sortOption) => {
        const params = new URLSearchParams(queryString);
        params.set('sort', sortOption);
        const url = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({ path: url }, '', url);
    };

    /**
     * Fetch products based on category/subcategory and current filters
     */
    const fetchDataWithFilters = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const queryString = buildQueryParams(filters);
            updateUrlWithQueryString(queryString, sortOption);

            let productsData = await fetchProductsByCategory(slug, queryString);

            if (!productsData || productsData.length === 0) {
                productsData = await fetchProductsBySubCategory(slug, queryString);
            }
            
            if (productsData && productsData.length > 0) {
                setCategoryName(productsData[0].Category);
                setCategoryImage(productsData[0].CategoryImage);
            }

            setAllProducts(productsData || []);
            setCurrentPage(0);
        } catch (error) {
            console.error('Error fetching products:', error);
            setError('Failed to fetch products. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Fetch products whenever filters or slug changes
     */
    useEffect(() => {
        fetchDataWithFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, slug]);

    /**
     * Helper function to determine effective display price
     */
    const getDisplayPrice = (product) => {
        return product.OfferPrice > 0 ? product.OfferPrice : product.Price;
    };

    /**
     * Handle sorting whenever sortOption or allProducts change
     */
    useEffect(() => {
        if (!isLoading && !error) {
            let sortedProducts = [...allProducts];

            switch (sortOption) {
                case 'price_low':
                    sortedProducts.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
                    break;
                case 'price_high':
                    sortedProducts.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
                    break;
                case 'latest':
                default:
                    sortedProducts.sort((a, b) => new Date(b.DateCreated) - new Date(a.DateCreated));
                    break;
            }

            setFilteredProducts(sortedProducts);
            setCurrentPage(0);
        }
    }, [sortOption, allProducts, isLoading, error]);

    /**
     * Handle browser navigation
     */
    useEffect(() => {
        const handlePopState = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const updatedFilters = {
                category: urlParams.get('category') || null,
                size: urlParams.get('size') || null,
                availability: urlParams.get('availability') || null,
                minPrice: urlParams.get('minprice') ? parseInt(urlParams.get('minprice'), 10) : null,
                maxPrice: urlParams.get('maxprice') ? parseInt(urlParams.get('maxprice'), 10) : null,
            };
            const updatedSort = urlParams.get('sort') || 'latest';
            setFilters(updatedFilters);
            setSortOption(updatedSort);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    /**
     * Handle pagination
     */
    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /**
     * Get current page data
     */
    const getPagedData = () => {
        if (!filteredProducts || filteredProducts.length === 0) {
            return [];
        }
        const offset = currentPage * itemsPerPage;
        return filteredProducts.slice(offset, offset + itemsPerPage);
    };

    const currentItems = getPagedData();
    const startItem = currentPage * itemsPerPage + 1;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, filteredProducts.length || 0);

    return (
        <div>
            {isLoading && (
                <div className="loading-container">
                    <Spinner />
                </div>
            )}
            {error && (
                <div className="error-container">
                    Error: {error}
                </div>
            )}
            {!isLoading && !error && (
                <>
                    <header className="header-image-container">
                        <img 
                            src={categoryImage ? `${CATEGORY_IMAGE_PATH}${categoryImage}` : DefaultHeader} 
                            alt={categoryName || "Category Header"} 
                            className="img-fluid category-header-image"
                        />
                    </header>
                    <div className="row mt-4 mx-0">
                        <h1 className="h4 heading">{categoryName || "All Products"}</h1>
                        <div className="col text-right">
                            <span className="items-count">
                                Showing {startItem} - {endItem} of {filteredProducts?.length || 0} results
                            </span>
                            <div className="product-dropdown-container">
                                <select
                                    className="custom-dropdown"
                                    value={sortOption}
                                    onChange={(e) => setSortOption(e.target.value)}
                                >
                                    <option value="latest">Sort By Latest</option>
                                    <option value="price_low">Price: Low to High</option>
                                    <option value="price_high">Price: High to Low</option>
                                </select>
                                <i className="fa-solid fa-chevron-down dropdown-icon"></i>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-3">
                                <FilterSidebar 
                                    onFilterChange={handleFilterChange} 
                                    initialFilters={filters} 
                                />
                            </div>
                            <div className="col-lg-9">
                                {currentItems.length === 0 ? (
                                    <div className="no-products">
                                        No products found. Please adjust your filters.
                                    </div>
                                ) : (
                                    <div className="product-list-grid">
                                        {currentItems.map(product => (
                                            <ProductCard key={product.ProductId} product={product} />
                                        ))}
                                    </div>
                                )}
                                {filteredProducts.length > 0 && (
                                    <div className="pagination-container">
                                        <ReactPaginate
                                            previousLabel={<i className="fa-solid fa-chevron-left"></i>}
                                            nextLabel={<i className="fa-solid fa-chevron-right"></i>}
                                            breakLabel="..."
                                            pageCount={Math.ceil(filteredProducts.length / itemsPerPage)}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={5}
                                            onPageChange={handlePageClick}
                                            containerClassName="pagination"
                                            activeClassName="active"
                                            pageClassName="page-item"
                                            pageLinkClassName="page-link"
                                            previousClassName="page-item"
                                            previousLinkClassName="page-link"
                                            nextClassName="page-item"
                                            nextLinkClassName="page-link"
                                            breakClassName="page-item"
                                            breakLinkClassName="page-link"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CategoryList;